// core/components/base-grid/base-grid.component.ts
import { Directive, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { CustomDialogService } from '../../services/custom-dialog.service';
import {
  GridService,
  GridServiceInstance,
} from '../../../shared/components/kendo/grid/services/grid.service';
import {
  GridDataResult,
  GridStateChangeEvent,
  GridConfig,
} from '../../../shared/components/kendo/grid/models/grid.model';
import { PortletButton } from '../../../shared/components/portlet/portlet/portlet.component';
import { FilterChangeEvent } from '../../../shared/components/kendo/grid/components/grid-filter/grid-filter.component';

@Directive()
export abstract class BaseGridComponent<
    T = any,
    TState extends { filter?: any; skip?: number } = any,
  >
  implements OnInit, OnDestroy
{
  protected readonly notificationService = inject(NotificationService);
  protected readonly dialogService = inject(CustomDialogService);
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly router = inject(Router);
  protected readonly gridServiceFactory = inject(GridService);
  protected readonly destroy$ = new Subject<void>();

  // Grid state
  gridConfig!: GridConfig;
  gridData: GridDataResult = { data: [], total: 0 };
  loading$ = new BehaviorSubject<boolean>(false);
  gridState: TState = this.getInitialGridState();
  portletButtons: PortletButton[] = [];

  // Grid service instance
  protected gridService?: GridServiceInstance;

  // Abstract properties
  abstract readonly gridId: string;
  abstract readonly apiEndpoint?: string;

  ngOnInit(): void {
    this.initializeComponent();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.gridService) {
      this.gridServiceFactory.destroyGrid(this.gridId);
    }
  }

  /**
   * Initialize component - override to customize
   */
  protected initializeComponent(): void {
    this.setupPortletButtons();
    this.setupGridConfig();
  }

  /**
   * Handle data state change from grid
   * This is now handled automatically by the grid service
   */
  onDataStateChange(state: GridStateChangeEvent): void {
    if (this.gridService) {
      // Just pass it to the grid service, which will handle everything
      this.gridService.handleDataStateChange(state);
    } else {
      // Fallback for grids without service
      this.handleManualStateChange(state);
    }

    // Update local state for reference
    this.gridState = {
      ...this.gridState,
      ...state,
    } as TState;
  }

  /**
   * Fallback for manual state handling (when grid service is not used)
   */
  protected handleManualStateChange(state: GridStateChangeEvent): void {
    this.gridState = {
      ...this.gridState,
      ...state,
    } as TState;
    this.loadData();
  }

  /**
   * Load data manually (for grids without service)
   */
  protected abstract loadData(): void;

  /**
   * Refresh data
   */
  refreshData(): void {
    if (this.gridService) {
      this.gridService.refreshData();
    } else {
      this.loadData();
    }
  }

  /**
   * Get initial grid state - override to customize
   */
  protected abstract getInitialGridState(): TState;

  /**
   * Setup portlet buttons - override to customize
   */
  protected abstract setupPortletButtons(): void;

  /**
   * Setup grid configuration - override to customize
   */
  protected abstract setupGridConfig(): void;

  /**
   * Handle add action - override to customize
   */
  protected abstract handleAdd(): void;

  onFilterChange(event: FilterChangeEvent): void {
    this.gridState.filter = event.kendoFilter;
    this.gridState.skip = 0;
    this.loadData();
  }
}
